import { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';

/**
 * Reusable modal wrapper component with smooth animations.
 * Handles backdrop, centering, escape key, and click-outside-to-close.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Modal content
 * @param {Function} props.onClose - Function to call when modal should close
 * @param {string} props.maxWidth - Tailwind max-width class (default: 'max-w-md')
 */
function Modal({ children, onClose, maxWidth }) {
  // Handle escape key to close
  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
      onClick={handleBackdropClick}
      role="presentation"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
    >
      <motion.div
        className={`bg-puzzle-surface rounded-2xl p-6 w-full ${maxWidth || 'max-w-md'} shadow-xl border border-puzzle-border`}
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ type: 'spring', duration: 0.3, bounce: 0.2 }}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

Modal.propTypes = {
  children: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
  maxWidth: PropTypes.string,
};

Modal.defaultProps = {
  maxWidth: 'max-w-md',
};

export default Modal;
